import { Input, Modal, ModalActions, ModalContent, ModalDescription, ModalHeading, ModalTrigger, Typography, Button } from "@launchmetrics/lm-ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setReportError, setReportName, setSaveReportModalOpen } from "store/article/report/reducers";
import { State } from "store/types";


export const SaveReportModal: React.FC = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const [reportError, reportName, isModalOpen, formDisabled] = useSelector((state: State) => [
		state.article.report.reportError,
		state.article.report.configuration.name!,
		state.article.report.saveReportModalOpen,
		state.article.report.formDisabled
	]);

	const [localReportName, setLocalReportName] = useState(reportName);

	const placeholderText = useMemo(() => {
		return !reportError
			? t('reports.side_panel.save_modal.input.placeholder.default')
			: t('reports.side_panel.save_modal.input.placeholder.error');
	}, [reportError, t]);

	useEffect(() => {
		if (!isModalOpen) {
			setLocalReportName('');
			dispatch(setReportError(''));
		}
	}, [dispatch, isModalOpen]);

	const reportErrorText = useMemo(() => reportError ? t(reportError) : '', [reportError, t]);

	const onSaveReport = useCallback(() => {
		dispatch(setReportName(localReportName));
		dispatch({ type: 'report/saveReport' });
	}, [dispatch, localReportName]);

	const handleKeyDown = useCallback((ev: React.KeyboardEvent<HTMLInputElement>) => {
		if (ev.key === 'Enter' && localReportName) {
			onSaveReport();
		}
	}, [localReportName, onSaveReport]);

	return useMemo(() => (
		<>
			<Modal variant="aligned" open={isModalOpen} onOpenChange={open => dispatch(setSaveReportModalOpen(open))}>
				<ModalTrigger>
					<Button
						name={t('reports.side_panel.footer.save')}
						onClick={() => dispatch(setSaveReportModalOpen(true))}
						variant='textGray'
						size='medium'
						disabled={formDisabled}
					>
						{t('reports.side_panel.footer.save')}
					</Button>
				</ModalTrigger>
				<ModalContent displayClose={false} styleProps={{ zIndex: 30 }} onPressEnter={() => localReportName && onSaveReport}>
					<ModalHeading>
						{t('reports.side_panel.save_modal.header')}
					</ModalHeading>
					<ModalDescription id={'save-report-modal-description'}>
						<Typography styleProps={{ color: "#5A5A60" }} variant='textMdRegular'>{t('reports.side_panel.save_modal.description')}</Typography>
						<Typography styleProps={{ marginTop: "24px", marginBottom: 2, display: 'flex' }} variant='textXsMedium'>
							{t('reports.side_panel.save_modal.input.label')}
							<Typography color={'text.error'} styleProps={{ paddingLeft: 2, fontSize: '12px', lineHeight: '16px'}}>*</Typography>
						</Typography>
						<Input
							id={'save-report-modal-input'}
							placeholder={placeholderText}
							error={reportErrorText}
							onChange={e => setLocalReportName(e.target.value)}
							onKeyDown={handleKeyDown}
							value={localReportName || ''}
						/>
					</ModalDescription>
					<ModalActions>
						<Button
							id={'save-report-modal-cancel-btn'} 
							variant="tertiary" 
							onClick={() => {
								dispatch(setSaveReportModalOpen(false));
								setLocalReportName('');
							}}
						>
							{t('reports.side_panel.save_modal.cancel')}
						</Button>
						<Button 
							id={'save-report-modal-save-btn'} 
							name={t('reports.side_panel.save_modal.save')} 
							disabled={!localReportName} 
							onClick={() => onSaveReport()}
						>
							{t('reports.side_panel.save_modal.save')}
						</Button>
					</ModalActions>
				</ModalContent>
			</Modal>
		</>
	), [isModalOpen, t, placeholderText, reportErrorText, handleKeyDown, localReportName, dispatch, onSaveReport, formDisabled]);
};
